/**
 * Real-time Notifications Hook
 * Manages WebSocket connection and notification state
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Get API base URL from environment
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Build WebSocket URL from API URL
const getWebSocketUrl = () => {
  try {
    // VITE_API_URL is like "http://localhost:5000/api" - we need "ws://localhost:5000/ws"
    const url = new URL(API_BASE);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}/ws`;  // WebSocket server is at /ws path
  } catch {
    return 'ws://localhost:5000/ws';
  }
};

const useNotifications = (token) => {
  // Always call hooks unconditionally - critical for React's Rules of Hooks
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [wsToken, setWsToken] = useState(null);
  const [error, setError] = useState(null);
  const ws = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = useRef(1000); // Start with 1 second

  // Get WebSocket token
  const getWebSocketToken = useCallback(async () => {
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE}/notifications/ws-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.ws_token;
      }
      return null;
    } catch (error) {
      console.error('Failed to get WebSocket token:', error);
      setError('Failed to get WebSocket token');
      return null;
    }
  }, [token]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    const wsTokenValue = await getWebSocketToken();
    if (!wsTokenValue) {

      setError('No WebSocket token available');
      return;
    }

    try {
      const wsUrl = `${getWebSocketUrl()}?token=${wsTokenValue}`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {

        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        reconnectDelay.current = 1000; // Reset delay

        // First authenticate with the token
        ws.current.send(JSON.stringify({
          type: 'authenticate',
          token: wsTokenValue
        }));
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'notification') {
            setNotifications(prev => [
              {
                id: Date.now(),
                ...data.payload,
                timestamp: new Date(),
                read: false
              },
              ...prev.slice(0, 49) // Keep only last 50 notifications
            ]);

            // Show browser notification if permitted
            if (Notification.permission === 'granted') {
              new Notification(data.payload.title || 'New Notification', {
                body: data.payload.message,
                icon: '/favicon.ico',
                badge: '/favicon.ico'
              });
            }
          }
        } catch (error) {

          setError('Failed to parse WebSocket message');
        }
      };

      ws.current.onclose = (event) => {

        setIsConnected(false);

        // Attempt to reconnect if not manually closed
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          setTimeout(() => {
            reconnectAttempts.current++;
            reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 30000);
            
            connect();
          }, reconnectDelay.current);
        }
      };

      ws.current.onerror = (error) => {

        setIsConnected(false);
        setError('WebSocket connection error');
      };

      setWsToken(wsTokenValue);

    } catch (error) {

      setIsConnected(false);
      setError('Failed to connect WebSocket');
    }
  }, [getWebSocketToken]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (ws.current) {
      ws.current.close(1000, 'Manual disconnect');
      ws.current = null;
    }
    setIsConnected(false);
    setWsToken(null);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      setError('Failed to mark notification as read');
    }
  }, [token]);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/notifications/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'test',
          message: 'This is a test notification from the notification system!'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send test notification');
      }

      return true;
    } catch (error) {
      console.error('Failed to send test notification:', error);
      setError('Failed to send test notification');
      return false;
    }
  }, [token]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);

  // Initialize connection when token is available
  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ws.current) {
        ws.current.close(1000, 'Component unmounted');
      }
    };
  }, []);

  return {
    notifications,
    isConnected,
    unreadCount: notifications.filter(n => !n.read).length,
    markAsRead,
    clearAll,
    sendTestNotification,
    requestNotificationPermission,
    connect,
    disconnect,
    error
  };
};

export default useNotifications;